/*
 * Torra — Subscription benefits popup + mobile drawer
 * Open/close, Esc, backdrop click, focus trap, mobile swipe-down, scroll-to-CTA.
 * Triggers are delegated, so a trigger placed anywhere on the page (e.g. a
 * Custom Liquid block inside the product form) keeps working even after the
 * theme re-renders that block on a variant change. No dependencies.
 */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var CLOSE_MS = 340;
  var controllers = [];
  var delegationBound = false;

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  function buildController(overlay) {
    var modal = overlay.querySelector('[data-torra-dialog]');
    var drawer = overlay.querySelector('[data-torra-dialog-mobile]');
    var handle = overlay.querySelector('[data-torra-handle]');
    var scrollTarget = overlay.getAttribute('data-torra-scroll-target') || '';
    var lastFocused = null;
    var isOpen = false;

    function activeDialog() {
      if (drawer && window.getComputedStyle(drawer).display !== 'none') { return drawer; }
      return modal;
    }

    function focusEl(el) {
      if (!el || typeof el.focus !== 'function') { return; }
      try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
    }

    function open() {
      if (isOpen) { return; }
      isOpen = true;
      lastFocused = document.activeElement;
      overlay.hidden = false;
      void overlay.offsetWidth; // force reflow so the open transition runs
      overlay.classList.add('open');
      if (!(window.Shopify && window.Shopify.designMode)) {
        document.body.style.overflow = 'hidden';
      }
      focusEl(activeDialog());
    }

    function close() {
      if (!isOpen) { return; }
      isOpen = false;
      overlay.classList.remove('open');
      document.body.style.overflow = '';
      window.setTimeout(function () {
        if (!isOpen) { overlay.hidden = true; }
      }, CLOSE_MS);
      focusEl(lastFocused);
    }

    function choose() {
      close();
      if (!scrollTarget) { return; }
      var el;
      try { el = document.querySelector(scrollTarget); }
      catch (e) { return; }
      if (!el) { return; }
      window.setTimeout(function () {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var t = (el.matches && el.matches(FOCUSABLE)) ? el : el.querySelector(FOCUSABLE);
        focusEl(t);
      }, 360);
    }

    function onKeydown(e) {
      if (!isOpen) { return; }
      if (e.key === 'Escape' || e.key === 'Esc') { close(); return; }
      if (e.key !== 'Tab') { return; }
      var dlg = activeDialog();
      if (!dlg) { return; }
      var nodes = Array.prototype.filter.call(
        dlg.querySelectorAll(FOCUSABLE),
        function (n) { return n.offsetWidth > 0 || n.offsetHeight > 0; }
      );
      if (!nodes.length) { e.preventDefault(); dlg.focus(); return; }
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (!dlg.contains(document.activeElement)) {
        e.preventDefault(); first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    // Closers / choosers live inside the static overlay — bind directly.
    Array.prototype.forEach.call(overlay.querySelectorAll('[data-torra-close]'), function (b) {
      b.addEventListener('click', close);
    });
    Array.prototype.forEach.call(overlay.querySelectorAll('[data-torra-choose]'), function (b) {
      b.addEventListener('click', choose);
    });
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) { close(); }
    });
    document.addEventListener('keydown', onKeydown);

    // Mobile: swipe-down on the handle / drawer to close.
    var startY = null;
    function onStart(e) {
      startY = e.touches ? e.touches[0].clientY : e.clientY;
      if (drawer) { drawer.style.transition = 'none'; }
    }
    function onMove(e) {
      if (startY === null || !drawer) { return; }
      var y = e.touches ? e.touches[0].clientY : e.clientY;
      var dy = y - startY;
      if (dy > 0) { drawer.style.transform = 'translateY(' + dy + 'px)'; }
    }
    function onEnd(e) {
      if (startY === null) { return; }
      var y = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
      var dy = y - startY;
      startY = null;
      if (drawer) {
        drawer.style.transition = '';
        drawer.style.transform = '';
      }
      if (dy > 90) { close(); }
    }
    // Only the handle reacts to swipe-down — listening on the whole drawer
    // would steal scroll gestures from the popup body.
    if (handle) {
      handle.addEventListener('touchstart', onStart, { passive: true });
      handle.addEventListener('touchmove', onMove, { passive: true });
      handle.addEventListener('touchend', onEnd);
    }

    // Theme editor: open this popup when the merchant selects the section.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', function (e) {
        if (e.detail && overlay.id === 'TorraPopup-' + e.detail.sectionId) { open(); }
      });
      document.addEventListener('shopify:section:deselect', function (e) {
        if (e.detail && overlay.id === 'TorraPopup-' + e.detail.sectionId) { close(); }
      });
    }

    return { overlay: overlay, open: open, close: close };
  }

  // Find which overlay a trigger should open.
  function resolveController(trigger) {
    var ac = trigger.getAttribute('aria-controls');
    if (ac) {
      for (var i = 0; i < controllers.length; i++) {
        if (controllers[i].overlay.id === ac) { return controllers[i]; }
      }
    }
    // Untargeted trigger inside a popup section -> that section's overlay.
    var section = trigger.closest ? trigger.closest('.torra-subscription-popup-section') : null;
    if (section) {
      for (var j = 0; j < controllers.length; j++) {
        if (section.contains(controllers[j].overlay)) { return controllers[j]; }
      }
    }
    // Otherwise, when there is just one popup on the page, use it.
    if (controllers.length === 1) { return controllers[0]; }
    return null;
  }

  function bindDelegation() {
    if (delegationBound) { return; }
    delegationBound = true;
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest ? e.target.closest('[data-torra-open]') : null;
      if (!trigger) { return; }
      var ctrl = resolveController(trigger);
      if (ctrl) { e.preventDefault(); ctrl.open(); }
    });
  }

  ready(function () {
    Array.prototype.forEach.call(document.querySelectorAll('[data-torra-overlay]'), function (overlay) {
      if (overlay.hasAttribute('data-torra-ready')) { return; }
      overlay.setAttribute('data-torra-ready', '');
      controllers.push(buildController(overlay));
    });
    bindDelegation();
  });
})();
